import React, { useMemo, useState } from "react";

const normalizeHref = (raw) => {
  const v = (raw || "").trim();
  if (!v) return "";

  const lower = v.toLowerCase();

  if (lower.startsWith("tel:") || lower.startsWith("mailto:")) return v;

  const phoneLike = /^\+?\d[\d\s-]{6,}$/.test(v);
  if (phoneLike) {
    const digits = v.replace(/[^\d]/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }

  if (lower.startsWith("wa:") || lower.startsWith("whatsapp:")) {
    const digits = v.split(":").slice(1).join(":").replace(/[^\d]/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }

  if (/^https?:\/\//i.test(v)) return v;
  if (lower.startsWith("www.")) return `https://${v}`;
  if (v.startsWith("/")) return v;

  return `https://${v}`;
};

const goToLink = (raw) => {
  const href = normalizeHref(raw);
  if (!href) return;

  const isExternal =
    /^https?:\/\//i.test(href) || /^mailto:/i.test(href) || /^tel:/i.test(href);

  if (isExternal) window.open(href, "_blank", "noopener,noreferrer");
  else window.location.href = href;
};

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 135;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return {
        style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` },
        className: "",
      };
    }
  }

  return { style: undefined, className: defaultClass };
};

const toList = (v) => {
  if (Array.isArray(v)) return v.filter(Boolean);
  if (typeof v === "string") return v.split(",").map((s) => s.trim()).filter(Boolean);
  return [];
};

const toHoursObject = (hours) => {
  if (hours && typeof hours === "object" && !Array.isArray(hours)) return hours;

  const h = String(hours || "").trim();
  if (!h) {
    return {
      monday: "",
      tuesday: "",
      wednesday: "",
      thursday: "",
      friday: "",
      saturday: "",
      sunday: "",
    };
  }

  return {
    monday: h,
    tuesday: h,
    wednesday: h,
    thursday: h,
    friday: h,
    saturday: h,
    sunday: h,
  };
};

const toCoords = (c) => {
  const lat = Number(c?.lat);
  const lng = Number(c?.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const cleanTel = (p) => String(p || "").replace(/[^\d+]/g, "");

const normalizeLocation = (loc) => {
  const hoursObj = toHoursObject(loc?.hours);

  return {
    name: loc?.name || loc?.businessName || "",
    address: loc?.address || "",
    city: loc?.city || "",
    state: loc?.state || "",
    zip: loc?.zip || "",
    country: loc?.country || "",
    phone: loc?.phone || "",
    email: loc?.email || "",
    manager: loc?.manager || "",
    mapLink: loc?.mapLink || loc?.directionLink || "",
    mapEmbedUrl: loc?.mapEmbedUrl || "",
    image:
      loc?.image ||
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1350&q=80",
    features: toList(loc?.features),
    hours: hoursObj,
    coordinates: toCoords(loc?.coordinates),
  };
};

const getTodayHours = (hoursObj) => {
  const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const today = days[new Date().getDay()];
  return hoursObj?.[today] || "Hours not available";
};

const buildDirectionsLink = (loc) => {
  if (loc?.mapLink) return loc.mapLink;

  if (loc?.coordinates) {
    return `https://www.google.com/maps/search/?api=1&query=${loc.coordinates.lat},${loc.coordinates.lng}`;
  }

  const addr = [loc?.address, loc?.city, loc?.state, loc?.zip, loc?.country]
    .filter(Boolean)
    .join(", ");
  if (addr) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;

  return "";
};

const buildMapEmbedSrc = (loc, apiKey) => {
  if (loc?.mapEmbedUrl) return loc.mapEmbedUrl;

  if (apiKey && loc?.coordinates) {
    const { lat, lng } = loc.coordinates;
    return `https://www.google.com/maps/embed/v1/view?key=${apiKey}&center=${lat},${lng}&zoom=15&maptype=roadmap`;
  }

  return "";
};

const buildStreetViewEmbedSrc = (loc, apiKey) => {
  if (!apiKey || !loc?.coordinates) return "";
  const { lat, lng } = loc.coordinates;
  return `https://www.google.com/maps/embed/v1/streetview?key=${apiKey}&location=${lat},${lng}&heading=210&pitch=10&fov=90`;
};

const pickAlign = (style, config, fallback) => {
  const v = String(style?.textAlign || style?.alignment || config?.alignment || "")
    .toLowerCase()
    .trim();
  if (v === "left" || v === "center" || v === "right") return v;
  return fallback;
};

const panelStyle = (bg, border) => ({
  backgroundColor: bg || undefined,
  borderColor: border || undefined,
});

const textColor = (value) => ({ color: value || undefined });

export default function LocationSection2({ config = {}, style = {} }) {
  const [mapTab, setMapTab] = useState("map");

  const bg = buildBg(style, "bg-gray-50");
  const apiKey = String(config.googleMapsApiKey || config.mapsApiKey || "").trim();

  const align = pickAlign(style, config, "center");
  const alignMap = { left: "text-left", center: "text-center", right: "text-right" };
  const alignClass = alignMap[align] || "text-center";

  const location = useMemo(() => {
    if (config.location && typeof config.location === "object") {
      return normalizeLocation(config.location);
    }

    if (Array.isArray(config.locations) && config.locations.length) {
      return normalizeLocation(config.locations[0]);
    }

    const hasOld =
      config.address || config.city || config.state || config.zip || config.phone || config.email || config.hours;

    if (hasOld) {
      return normalizeLocation({
        name: config.businessName || config.name || "Our Location",
        address: config.address || "",
        city: config.city || "",
        state: config.state || "",
        zip: config.zip || "",
        country: config.country || "",
        phone: config.phone || "",
        email: config.email || "",
        hours: config.hours || "",
        mapLink: config.directionLink || "",
        mapEmbedUrl: config.mapEmbedUrl || "",
        coordinates: config.coordinates || null,
        image: config.image || "",
        features: config.features || [],
      });
    }

    return null;
  }, [
    config.location,
    config.locations,
    config.businessName,
    config.name,
    config.address,
    config.city,
    config.state,
    config.zip,
    config.country,
    config.phone,
    config.email,
    config.hours,
    config.directionLink,
    config.mapEmbedUrl,
    config.coordinates,
    config.image,
    config.features,
  ]);

  const addressLine = location
    ? [location.address, location.city, location.state, location.zip, location.country]
        .filter(Boolean)
        .join(", ")
    : "";

  const directionsLink = location ? buildDirectionsLink(location) : "";
  const mapSrc = location ? buildMapEmbedSrc(location, apiKey) : "";
  const streetSrc = location ? buildStreetViewEmbedSrc(location, apiKey) : "";
  const canStreet = !!apiKey && !!location?.coordinates;

  const activeTabStyle = {
    backgroundColor: style?.locationMapTabActiveBg || undefined,
    color: style?.locationMapTabActiveText || undefined,
    borderColor: style?.locationMapTabBorder || undefined,
  };

  const inactiveTabStyle = {
    backgroundColor: style?.locationMapTabInactiveBg || undefined,
    color: style?.locationMapTabInactiveText || undefined,
    borderColor: style?.locationMapTabBorder || undefined,
  };

  return (
    <section dir={config?.direction || "ltr"} style={bg.style} className={`py-20 px-4 ${bg.className}`}>
      <div className="container mx-auto max-w-7xl">
        <div className={`mb-12 ${alignClass}`}>
          <h2 style={{ color: style?.titleColor || undefined }} className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {config.title || "Visit Us"}
          </h2>
          <p style={{ color: style?.subtitleColor || undefined }} className="text-xl text-gray-600 max-w-3xl mx-auto">
            {config.subtitle || "Find our location and contact details below"}
          </p>
        </div>

        {!location ? (
          <div
            className="text-center py-12 rounded-2xl border shadow-sm"
            style={panelStyle(style?.locationCardBg || "#ffffff", style?.locationCardBorder)}
          >
            <div className="text-5xl mb-4">📍</div>
            <h3 className="text-xl font-bold mb-2" style={textColor(style?.locationTextColor)}>
              Location not set
            </h3>
            <p style={textColor(style?.locationMutedTextColor)}>
              Add location info in the editor to show the map and details.
            </p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <div
                className="rounded-2xl shadow-xl overflow-hidden border"
                style={panelStyle(style?.locationCardBg || "#ffffff", style?.locationCardBorder)}
              >
                <div className="relative h-64">
                  <img src={location.image} alt={location.name || "Location"} className="w-full h-full object-cover" />
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        style?.locationImageOverlayColor ||
                        "linear-gradient(to top, rgba(0,0,0,0.70), rgba(0,0,0,0.30), transparent)",
                    }}
                  />
                  <div className={`absolute bottom-4 left-6 right-6 text-white ${alignClass}`}>
                    <h3
                      className="text-2xl font-bold mb-1"
                      style={textColor(style?.locationNameColor || "#ffffff")}
                    >
                      {location.name || "Our Location"}
                    </h3>
                    {addressLine ? (
                      <p className="flex items-center gap-2 text-sm opacity-90">
                        <span>📍</span>
                        <span className="truncate">{addressLine}</span>
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className={`p-6 space-y-6 ${alignClass}`}>
                  <div
                    className="rounded-2xl border p-4 text-start"
                    style={panelStyle(style?.locationPanelBg, style?.locationPanelBorder)}
                  >
                    <div
                      className="font-semibold mb-3 flex items-center gap-2"
                      style={textColor(style?.locationTextColor)}
                    >
                      <span style={textColor(style?.locationIconColor)}>📞</span> Contact
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between gap-4">
                        <span style={textColor(style?.locationMutedTextColor)}>Phone</span>
                        {location.phone ? (
                          <a
                            href={`tel:${cleanTel(location.phone)}`}
                            className="font-semibold hover:underline"
                            style={textColor(style?.locationTextColor || "#2563eb")}
                          >
                            {location.phone}
                          </a>
                        ) : (
                          <span style={textColor(style?.locationMutedTextColor)}>Not set</span>
                        )}
                      </div>

                      <div className="flex justify-between gap-4">
                        <span style={textColor(style?.locationMutedTextColor)}>Email</span>
                        {location.email ? (
                          <a
                            href={`mailto:${location.email}`}
                            className="font-semibold hover:underline break-all text-right"
                            style={textColor(style?.locationTextColor || "#2563eb")}
                          >
                            {location.email}
                          </a>
                        ) : (
                          <span style={textColor(style?.locationMutedTextColor)}>Not set</span>
                        )}
                      </div>

                      {location.manager ? (
                        <div className="flex justify-between gap-4">
                          <span style={textColor(style?.locationMutedTextColor)}>Contact person</span>
                          <span className="font-semibold" style={textColor(style?.locationTextColor)}>
                            {location.manager}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div
                    className="rounded-2xl border p-4 text-start"
                    style={panelStyle(style?.locationPanelBg, style?.locationPanelBorder)}
                  >
                    <div
                      className="font-semibold mb-3 flex items-center gap-2"
                      style={textColor(style?.locationTextColor)}
                    >
                      <span style={textColor(style?.locationIconColor)}>🕒</span> Working Hours
                    </div>

                    <div className="space-y-1 text-sm">
                      {Object.entries(location.hours || {}).map(([day, h]) => (
                        <div key={day} className="flex justify-between gap-4">
                          <span className="capitalize" style={textColor(style?.locationMutedTextColor)}>
                            {day}
                          </span>
                          <span className="font-medium" style={textColor(style?.locationTextColor)}>
                            {h || "—"}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div
                      className="mt-3 p-2 rounded-lg"
                      style={{
                        backgroundColor: style?.locationTodayBg || "#dcfce7",
                        color: style?.locationTodayText || "#15803d",
                      }}
                    >
                      <p className="text-sm">
                        <span className="font-semibold">Today:</span> {getTodayHours(location.hours)}
                      </p>
                    </div>
                  </div>

                  {location.features?.length ? (
                    <div
                      className="rounded-2xl border p-4 text-start"
                      style={panelStyle(style?.locationPanelBg, style?.locationPanelBorder)}
                    >
                      <div
                        className="font-semibold mb-3 flex items-center gap-2"
                        style={textColor(style?.locationTextColor)}
                      >
                        <span style={textColor(style?.locationIconColor)}>✅</span> Highlights
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {location.features.map((f, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 rounded-full text-xs font-semibold border"
                            style={{
                              backgroundColor: style?.locationBadgeBg || "#eff6ff",
                              color: style?.locationBadgeTextColor || "#1d4ed8",
                              borderColor: style?.locationBadgeBorder || "transparent",
                            }}
                          >
                            {f}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={() => directionsLink && goToLink(directionsLink)}
                      style={{
                        backgroundColor: style?.primaryBtnBg || undefined,
                        color: style?.primaryBtnText || undefined,
                      }}
                      className={`flex-1 font-semibold py-3 px-4 rounded-lg transition ${
                        style?.primaryBtnBg ? "hover:opacity-90" : "bg-blue-600 hover:bg-blue-700 text-white"
                      }`}
                    >
                      Get Directions
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        if (!addressLine) return;
                        if (!navigator?.clipboard?.writeText) return;
                        navigator.clipboard.writeText(addressLine).catch(() => {});
                      }}
                      style={{
                        backgroundColor: style?.ctaSecondaryBtnBg || undefined,
                        color: style?.secondaryBtnText || undefined,
                        borderColor: style?.secondaryBtnBorder || undefined,
                      }}
                      className={`flex-1 font-semibold py-3 px-4 rounded-lg border-2 transition ${
                        style?.secondaryBtnBorder || style?.secondaryBtnText || style?.ctaSecondaryBtnBg
                          ? "hover:opacity-90"
                          : "border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Copy Address
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div
                className="rounded-2xl shadow-xl overflow-hidden border"
                style={panelStyle(style?.locationCardBg || "#ffffff", style?.locationCardBorder)}
              >
                <div
                  className="px-5 py-4 border-b flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                  style={{ borderColor: style?.locationCardBorder || undefined }}
                >
                  <div className="font-semibold" style={textColor(style?.locationTextColor)}>
                    Map
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setMapTab("map")}
                      style={mapTab === "map" ? activeTabStyle : inactiveTabStyle}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${
                        mapTab === "map" && !style?.locationMapTabActiveBg && !style?.locationMapTabActiveText
                          ? "bg-blue-600 text-white border-blue-600"
                          : mapTab !== "map" &&
                            !style?.locationMapTabInactiveBg &&
                            !style?.locationMapTabInactiveText
                          ? "bg-white text-gray-700 hover:bg-gray-50"
                          : ""
                      }`}
                    >
                      Map View
                    </button>

                    <button
                      type="button"
                      onClick={() => setMapTab("street")}
                      disabled={!canStreet}
                      title={!canStreet ? "Add coordinates and an API key to enable Street View" : ""}
                      style={mapTab === "street" ? activeTabStyle : inactiveTabStyle}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${
                        !canStreet ? "opacity-50 cursor-not-allowed" : ""
                      } ${
                        mapTab === "street" && canStreet && !style?.locationMapTabActiveBg && !style?.locationMapTabActiveText
                          ? "bg-blue-600 text-white border-blue-600"
                          : mapTab !== "street" &&
                            !style?.locationMapTabInactiveBg &&
                            !style?.locationMapTabInactiveText
                          ? "bg-white text-gray-700 hover:bg-gray-50"
                          : ""
                      }`}
                    >
                      Street View
                    </button>
                  </div>
                </div>

                <div className="h-[460px] bg-gray-100">
                  {mapTab === "street" && canStreet && streetSrc ? (
                    <iframe
                      title={`Street View: ${location.name || "Location"}`}
                      src={streetSrc}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      className="w-full h-full"
                    />
                  ) : mapSrc ? (
                    <iframe
                      title={`Map: ${location.name || "Location"}`}
                      src={mapSrc}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      className="w-full h-full"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center p-6 text-center">
                      <div>
                        <div className="text-4xl mb-2">🗺️</div>
                        <div className="font-semibold" style={textColor(style?.locationTextColor)}>
                          Map preview unavailable
                        </div>
                        <div className="text-sm mt-1" style={textColor(style?.locationMutedTextColor)}>
                          Add coordinates and an API key, or provide mapEmbedUrl.
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {!apiKey ? (
                  <div
                    className="px-5 py-3 text-xs bg-gray-50"
                    style={textColor(style?.locationMutedTextColor)}
                  >
                    Tip: add config.googleMapsApiKey and coordinates to enable map and Street View automatically.
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}